import React from 'react';
import AlbumCoverCard, { CARD_SIZE } from '../AlbumCoverCard';
import SectionTitle from '../SectionTitle';
import HScrollRow from '../HScrollRow';
import { localCatalogResultCount } from '../../services/unifiedSearchLocal';

function LibraryBadge() {
  return (
    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Library</span>
  );
}

/** Shared Library Artists / Albums / Tracks blocks for top search UIs. */
export function LocalCatalogSearchSectionsSlideout({
  localCatalog,
  searchTab,
  ArtistRow,
  AlbumRow,
  LocalTrackRow,
  SectionHeader,
  onSelectLocalArtist,
  onSelectLocalAlbum,
  onSelectLocalTrack,
}) {
  if (!localCatalog) return null;
  return (
    <>
      {(searchTab === 'all' || searchTab === 'artists') && localCatalog.artists.length > 0 && (
        <>
          <SectionHeader title="Library Artists" count={localCatalog.artists.length} />
          {localCatalog.artists.map((a, i) => (
            <ArtistRow
              key={`local-artist-${a.id || i}`}
              artist={{ ...a, source: 'local', sourceLabel: a.sourceLabel || 'Library' }}
              onSelect={onSelectLocalArtist}
            />
          ))}
        </>
      )}
      {(searchTab === 'all' || searchTab === 'albums') && localCatalog.albums.length > 0 && (
        <>
          <SectionHeader title="Library Albums" count={localCatalog.albums.length} />
          {localCatalog.albums.map((al, i) => (
            <AlbumRow
              key={`local-album-${al.id || i}`}
              album={{
                id: al.id,
                title: al.title,
                artistName: al.artistName,
                source: 'local',
                sourceLabel: al.sourceLabel || 'Library',
              }}
              onSelect={onSelectLocalAlbum}
            />
          ))}
        </>
      )}
      {(searchTab === 'all' || searchTab === 'tracks') && localCatalog.tracks.length > 0 && (
        <>
          <SectionHeader title="Library Tracks" count={localCatalog.tracks.length} />
          {localCatalog.tracks.map((tr, i) => (
            <LocalTrackRow key={`local-${tr.id || i}`} track={tr} onSelect={onSelectLocalTrack} />
          ))}
        </>
      )}
    </>
  );
}

export function LocalCatalogSearchSectionsPage({ localCatalog, nav, onPlayTrack }) {
  if (!localCatalog || localCatalogResultCount(localCatalog) === 0) return null;
  return (
    <>
      {localCatalog.artists.length > 0 && (
        <div className="mb-8">
          <SectionTitle title="Library Artists" />
          <div className="space-y-1">
            {localCatalog.artists.map((a) => (
              <button
                key={`local-artist-${a.id}`}
                type="button"
                onClick={() => nav.openArtist({ id: a.id, name: a.name, __source: 'local_library' })}
                className="flex items-center gap-3 w-full text-left py-2 px-3 hover:bg-white/5 rounded-xl transition"
              >
                <div className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center bg-white/5 text-white/30">♪</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{a.name}</p>
                </div>
                <LibraryBadge />
              </button>
            ))}
          </div>
        </div>
      )}
      {localCatalog.albums.length > 0 && (
        <div className="mb-8">
          <SectionTitle title="Library Albums" />
          <HScrollRow expanded cardSize={CARD_SIZE}>
            {localCatalog.albums.map((album, i) => (
              <AlbumCoverCard
                key={album.id}
                album={{
                  id: album.id,
                  title: album.title,
                  artistName: album.artistName,
                  source: 'local_library',
                }}
                onClick={() => nav.openAlbum({
                  id: album.id,
                  album_key: album.album_key || album.id,
                  title: album.title,
                  artist_name: album.artistName,
                  __source: 'local_library',
                  source: 'local_library',
                })}
                animDelay={i * 30}
              />
            ))}
          </HScrollRow>
        </div>
      )}
      {localCatalog.tracks.length > 0 && (
        <div className="mb-8">
          <SectionTitle title="Library Tracks" />
          <div className="space-y-1">
            {localCatalog.tracks.map((tr) => (
              <button
                key={`local-${tr.id}`}
                type="button"
                onClick={() => onPlayTrack(tr)}
                className="flex items-center gap-3 w-full text-left py-2 px-3 hover:bg-white/5 rounded-xl transition"
              >
                <div className="w-11 h-11 rounded-lg flex-shrink-0 flex items-center justify-center bg-white/5 text-white/30">♫</div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{tr.title}</p>
                  <p className="text-xs text-white/40 truncate">{tr.artist}{tr.album ? ` — ${tr.album}` : ''}</p>
                </div>
                <LibraryBadge />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
